'use client';

import type { MysteryRoomImpactEffectState, MysteryRoomLocalBotState, MysteryRoomLocalRuntime, MysteryRoomNpcState, MysteryRoomNpcTaskState } from './localRuntime';
import {
  advanceAllIncidentResolutions,
  assignIncidentIfOwnedByTask,
  beginIncidentResolution,
  seedSystemIncidents,
  updateWorldAlarmFromIncidents,
} from '../systems/incidentSystem';
import { updateDoors, type DoorActor } from '../systems/doorSystem';
import { resolveZoneId, stepActor, updateHeroMovement } from '../systems/movementSystem';
import { updateNpcBehavior } from '../systems/npcSystem';
import { updateNpcDetection } from '../systems/detectionSystem';
import { buildActorRouteToPoint, buildNpcNavPath, resolveImmediateRouteWaypoint, updateNpcNavigation } from '../systems/navigationSystem';
import {
  buildNpcTaskQueue,
  chooseNpcTask,
  defaultRoleTask,
  shouldReleaseCurrentTask,
  shouldReplaceCurrentTask,
} from '../systems/npcTaskSystem';
import { updateSecuritySystems } from '../systems/securitySystem';
import { updateLocalTriggers } from '../systems/triggerSystem';
import { buildSightBlockingSegments, buildVisibilityState, hasUnobstructedSightLine } from '../systems/visibilitySystem';
import { updateMysteryRoomCombat } from '../systems/combatSystem';
import type { MysteryRoomAccessLevel } from '../world/mapTypes';
import { npcCanAttackHero, npcCanTargetNpc, npcUsesZombieBehavior } from '../systems/npcBehaviorTreeSystem';

/** Задержка возрождения на контрольной точке (мс); экспорт для HUD. */
export const MYSTERY_ROOM_HERO_RESPAWN_DELAY_MS = 5_000;
const HERO_RESPAWN_DELAY_MS = MYSTERY_ROOM_HERO_RESPAWN_DELAY_MS;
const LOCAL_BOT_CHASE_ROUTE_MS = 340;
const LOCAL_BOT_CHASE_GOAL_SHIFT = 96;

export function updateMysteryRoomLocalRuntime(
  runtime: MysteryRoomLocalRuntime,
  dtMs: number,
  options?: { skipLocalTriggers?: boolean; extraDoorActors?: DoorActor[] },
): MysteryRoomLocalRuntime {
  if (runtime.gameStatus === 'respawning') {
    return advanceHeroRespawn(runtime, dtMs);
  }
  if (runtime.gameStatus === 'game_over' || !runtime.hero.alive) {
    return finalizeHeroLife(runtime);
  }

  const movementResult = updateHeroMovement(
    runtime.map,
    runtime.hero,
    runtime.input,
    runtime.doors,
    dtMs,
    runtime.inventory.filter((item) => (item.quantity ?? 1) > 0).map((item) => item.id),
  );
  const activeMap = movementResult.map;
  const hero =
    movementResult.hero.movementMode === 'idle'
      ? movementResult.hero
      : {
          ...movementResult.hero,
          hidden: false,
          hideZoneId: null,
        };
  const baseDoorActors: DoorActor[] = [
    actorBoundsForCircle(
      'hero',
      hero.position,
      hero.radius,
      hero.accessLevels,
      'hero',
      runtime.inventory.map((item) => item.id),
    ),
    ...runtime.npcs
      .filter((npc) => npc.alive)
      .map((npc) => actorBoundsForCircle(npc.id, npc.position, npc.radius, npc.accessLevels, 'npc')),
  ];
  const nextDoors = updateDoors(runtime.doors, [...baseDoorActors, ...(options?.extraDoorActors ?? [])], dtMs);
  const elapsedMs = runtime.elapsedMs + dtMs;
  const seededRuntime = seedSystemIncidents(
    {
      ...runtime,
      map: activeMap,
      hero,
      doors: nextDoors,
    },
    dtMs,
  );
  const securityRuntime = updateSecuritySystems({
    ...seededRuntime,
    elapsedMs,
  });
  const worldAlarm = updateWorldAlarmFromIncidents(securityRuntime.worldAlarm, securityRuntime.incidents, elapsedMs);
  const runtimeForTasks = {
    ...securityRuntime,
    elapsedMs,
    worldAlarm,
  };
  const taskAssignedNpcs = runtimeForTasks.npcs.map((npc) =>
    npc.alive ? resolveNpcTask(runtimeForTasks, npc, nextDoors) : npc,
  );
  let incidents = runtimeForTasks.incidents;
  for (const npc of taskAssignedNpcs) {
    incidents = assignIncidentIfOwnedByTask(incidents, npc, elapsedMs);
  }
  const navigatedNpcs = taskAssignedNpcs.map((npc) =>
    npc.alive ? updateNpcNavigation(runtimeForTasks.map, nextDoors, npc, dtMs) : npc,
  );
  const movedNpcs = navigatedNpcs.map((npc) =>
    npc.alive ? updateNpcBehavior(npc, runtimeForTasks.map, nextDoors, dtMs, { hero, npcs: navigatedNpcs }) : npc,
  );
  const zombieContact = applyZombieContactDamage(hero, movedNpcs, runtime.impactEffects, runtime.events, elapsedMs);
  const hostileContact = applyHostileNpcContactDamage(
    zombieContact.hero,
    zombieContact.npcs,
    zombieContact.impactEffects,
    zombieContact.events,
    elapsedMs,
  );
  const resolvedIncidents = advanceAllIncidentResolutions(incidents, dtMs, elapsedMs);
  const syncedIncidents = resolvedIncidents.map((incident) => {
    const resolver = hostileContact.npcs.find(
      (npc) =>
        npc.currentTask?.assignedIncidentId === incident.id &&
        npc.currentRoleAction &&
        npc.id === incident.assignedNpcId,
    );
    if (!resolver || incident.status !== 'assigned') {
      return incident;
    }

    return beginIncidentResolution(
      incident,
      resolver.id,
      resolver.currentRoleAction,
      resolver.roleActionRemainingMs,
    );
  });
  const injuryHealing = applyResolvedInjuryHealing(hostileContact.npcs, syncedIncidents, hostileContact.events);
  const finalWorldAlarm = updateWorldAlarmFromIncidents(worldAlarm, syncedIncidents, elapsedMs);
  const detectionResult = updateNpcDetection(
    {
      ...runtimeForTasks,
      hero: hostileContact.hero,
      doors: nextDoors,
      worldAlarm: finalWorldAlarm,
      incidents: syncedIncidents,
      npcs: injuryHealing.npcs,
    },
    dtMs,
  );
  const contactEvents = injuryHealing.events.filter((event) => !runtime.events.includes(event));
  const nextEvents = contactEvents.length > 0
    ? [...contactEvents, ...detectionResult.events].slice(0, 10)
    : detectionResult.events;

  const nextRuntime = {
    ...runtime,
    map: activeMap,
    elapsedMs,
    hero: detectionResult.hero,
    doors: nextDoors,
    npcs: detectionResult.npcs,
    impactEffects: hostileContact.impactEffects,
    incidents: syncedIncidents,
    worldAlarm: finalWorldAlarm,
    security: runtimeForTasks.security,
    events: nextEvents,
    visibility: buildVisibilityState({
      ...runtimeForTasks,
      map: activeMap,
      hero: detectionResult.hero,
      doors: nextDoors,
      npcs: detectionResult.npcs,
    }),
    input: {
      ...runtime.input,
      pointerTarget: movementResult.pointerTarget,
    },
  };

  const triggeredRuntime = options?.skipLocalTriggers ? nextRuntime : updateLocalTriggers(nextRuntime);
  const combatRuntime = updateMysteryRoomCombat(
    {
      ...triggeredRuntime,
      npcTaskQueue: buildNpcTaskQueue(triggeredRuntime),
    },
    dtMs,
  );
  const localBotTick = updateLocalBotsTick(combatRuntime, dtMs);
  const withLocalBots: MysteryRoomLocalRuntime =
    combatRuntime.localBots && combatRuntime.localBots.length > 0
      ? {
          ...combatRuntime,
          localBots: localBotTick.localBots,
          hero: localBotTick.hero,
          impactEffects: localBotTick.impactEffects,
          events: localBotTick.events,
        }
      : combatRuntime;

  const zombieRuntime = updateZombieWaveController(withLocalBots);
  const missionRuntime = updateGlobalMissionProgress(zombieRuntime);
  return finalizeHeroLife(missionRuntime);
}

function updateZombieWaveController(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  const state = runtime.zombieMode;
  if (!state.enabled || state.completed || runtime.gameplay.gameMode !== 'infection') {
    return runtime;
  }

  let zombieMode = { ...state };
  let localBots = runtime.localBots;
  let events = runtime.events;
  let changed = false;

  const startWave = (waveNumber: number) => {
    const waveLoad = zombieMode.waveSize + Math.floor((waveNumber - 1) * 1.5);
    zombieMode = {
      ...zombieMode,
      currentWave: waveNumber,
      remainingToSpawn: waveLoad,
      nextSpawnAtMs: runtime.elapsedMs,
      nextWaveAtMs: null,
    };
    events = [`Волна ${waveNumber}/${zombieMode.totalWaves}: заражённые приближаются`, ...events].slice(0, 10);
    changed = true;
  };

  if (zombieMode.currentWave === 0 && runtime.elapsedMs >= zombieMode.nextSpawnAtMs) {
    startWave(1);
  }

  if (zombieMode.remainingToSpawn > 0 && runtime.elapsedMs >= zombieMode.nextSpawnAtMs) {
    const bot = spawnZombieWaveBot(runtime, zombieMode.currentWave, zombieMode.remainingToSpawn);
    localBots = [bot, ...localBots].slice(0, 84);
    zombieMode = {
      ...zombieMode,
      remainingToSpawn: Math.max(0, zombieMode.remainingToSpawn - 1),
      nextSpawnAtMs: runtime.elapsedMs + zombieMode.spawnIntervalMs,
    };
    changed = true;
  }

  const activeThreats = localBots.filter((bot) => !bot.remotePeer).length;
  if (zombieMode.currentWave > 0 && zombieMode.remainingToSpawn === 0 && activeThreats === 0) {
    if (zombieMode.currentWave >= zombieMode.totalWaves) {
      zombieMode = {
        ...zombieMode,
        completed: true,
        nextSpawnAtMs: Number.POSITIVE_INFINITY,
        nextWaveAtMs: null,
      };
      events = ['Все волны заражённых уничтожены', ...events].slice(0, 10);
    } else {
      const nextWave = zombieMode.currentWave + 1;
      if (zombieMode.nextWaveAtMs == null) {
        zombieMode = {
          ...zombieMode,
          nextWaveAtMs: runtime.elapsedMs + zombieMode.waveBreakMs,
        };
      }
      if (zombieMode.nextWaveAtMs != null && runtime.elapsedMs >= zombieMode.nextWaveAtMs) {
        startWave(nextWave);
      }
    }
    changed = true;
  }

  if (!changed) {
    return runtime;
  }

  return {
    ...runtime,
    zombieMode,
    localBots,
    events,
  };
}

function updateGlobalMissionProgress(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  const mission = runtime.globalMission;
  if (!mission.active) {
    return runtime;
  }
  if (mission.key === 'evac') {
    const sampleCollected = runtime.interactables.some(
      (entry) => entry.id === 'research_sample_case' && entry.state === 'used',
    );
    const inAirlock = runtime.hero.zoneId === 'airlock_entry';
    const current = Number(sampleCollected) + Number(sampleCollected && inAirlock);
    const completed = current >= mission.target;
    return withUpdatedMission(runtime, current, completed);
  }
  if (mission.key === 'last_voice') {
    const serverAudited = runtime.interactables.some(
      (entry) => entry.id === 'server_audit_terminal' && entry.state === 'used',
    );
    const reactorChecked = runtime.interactables.some(
      (entry) => entry.id === 'reactor_console' && entry.state === 'used',
    );
    const current = Number(serverAudited) + Number(reactorChecked);
    const completed = current >= mission.target;
    return withUpdatedMission(runtime, current, completed);
  }

  const hostileNpcCount = runtime.npcs.filter(
    (npc) =>
      npc.alive &&
      (npcCanAttackHero(npc) ||
        runtime.npcs.some((otherNpc) => otherNpc.id !== npc.id && otherNpc.alive && npcCanTargetNpc(npc, otherNpc))),
  ).length;
  const hostileBotCount = runtime.localBots.filter((bot) => !bot.remotePeer).length;

  // Зачем: если в секторе нет угроз, миссия "Зачистка" не должна мгновенно завершаться.
  const baseThreatProfileCount = runtime.npcs.filter(
    (npc) =>
      npcCanAttackHero(npc) ||
      runtime.npcs.some((otherNpc) => otherNpc.id !== npc.id && npcCanTargetNpc(npc, otherNpc)),
  ).length;
  const remainingThreats = hostileNpcCount + hostileBotCount;

  if (runtime.zombieMode.enabled) {
    const totalWaveThreats = countZombieWavePlan(runtime.zombieMode.waveSize, runtime.zombieMode.totalWaves);
    const completedWavesThreats = countZombieWavePlan(
      runtime.zombieMode.waveSize,
      Math.max(0, runtime.zombieMode.currentWave - 1),
    );
    const currentWaveLoad = zombieWaveLoad(runtime.zombieMode.waveSize, runtime.zombieMode.currentWave);
    const spawnedCurrentWave = Math.max(0, currentWaveLoad - runtime.zombieMode.remainingToSpawn);
    const spawnedTotal = Math.max(0, completedWavesThreats + spawnedCurrentWave);
    const current = Math.max(0, Math.min(totalWaveThreats, spawnedTotal - remainingThreats));
    const completed = runtime.zombieMode.completed && remainingThreats === 0;
    return withUpdatedMission(runtime, current, completed, Math.max(mission.target, totalWaveThreats, 1));
  }

  if (baseThreatProfileCount === 0 && hostileBotCount === 0) {
    return withUpdatedMission(runtime, 0, false, Math.max(mission.target, 1));
  }

  const target = Math.max(mission.target, baseThreatProfileCount, 1);
  const current = Math.max(0, target - remainingThreats);
  const completed = remainingThreats === 0 && target > 0;
  return withUpdatedMission(runtime, current, completed, target);
}

function countZombieWavePlan(waveSize: number, totalWaves: number): number {
  let total = 0;
  for (let wave = 1; wave <= totalWaves; wave += 1) {
    total += zombieWaveLoad(waveSize, wave);
  }
  return total;
}

function zombieWaveLoad(waveSize: number, wave: number): number {
  if (wave <= 0) {
    return 0;
  }
  return waveSize + Math.floor((wave - 1) * 1.5);
}

function withUpdatedMission(
  runtime: MysteryRoomLocalRuntime,
  current: number,
  completed: boolean,
  targetOverride?: number,
): MysteryRoomLocalRuntime {
  const target = targetOverride ?? runtime.globalMission.target;
  const progressChanged = current !== runtime.globalMission.current || completed !== runtime.globalMission.completed || target !== runtime.globalMission.target;
  if (!progressChanged) {
    return runtime;
  }
  const completionNow = completed && !runtime.globalMission.completed;
  const rewardMoney = completionNow ? runtime.globalMission.rewardMoney : 0;
  return {
    ...runtime,
    gameStatus: completionNow ? 'game_over' : runtime.gameStatus,
    globalMission: {
      ...runtime.globalMission,
      current: Math.min(target, Math.max(0, current)),
      target,
      completed,
    },
    hero: completionNow
      ? {
          ...runtime.hero,
          money: runtime.hero.money + rewardMoney,
        }
      : runtime.hero,
    events: completionNow
      ? [
          `Глобальная миссия выполнена: ${runtime.globalMission.title} (+${rewardMoney} CR)`,
          ...runtime.events,
        ].slice(0, 10)
      : runtime.events,
  };
}

function spawnZombieWaveBot(
  runtime: MysteryRoomLocalRuntime,
  waveNumber: number,
  remainingInWave: number,
): MysteryRoomLocalBotState {
  const botSpawnPool = runtime.map.spawnSlots.filter((slot) => slot.actor === 'bot');
  const spawnPool = botSpawnPool.length > 0
    ? botSpawnPool
    : runtime.map.spawnSlots.length > 0
      ? runtime.map.spawnSlots
      : [{ x: runtime.map.spawn.x, y: runtime.map.spawn.y, room: runtime.map.spawnZoneId }];
  const slot = spawnPool[Math.floor(Math.random() * spawnPool.length)] ?? spawnPool[0];
  const jitterX = (Math.random() - 0.5) * 96;
  const jitterY = (Math.random() - 0.5) * 96;
  const spawn = {
    x: slot.x + jitterX,
    y: slot.y + jitterY,
  };
  const botId = `z_wave_${waveNumber}_${remainingInWave}_${runtime.elapsedMs}`;
  return {
    id: botId,
    name: `Зомби W${waveNumber}`,
    position: spawn,
    velocity: { x: 0, y: 0 },
    facing: 'down',
    zoneId: slot.room || slot.zoneId || runtime.map.spawnZoneId,
    target: { ...runtime.hero.position },
    speed: 62 + Math.min(22, waveNumber * 2),
    radius: 16,
    scale: 1.05,
    color: 0x84cc16,
    animationState: 'idle',
    decisionAtMs: runtime.elapsedMs,
    lastAttackAtMs: 0,
    aggression: 1.3 + Math.min(0.9, waveNumber * 0.06),
  };
}

// Зачем: простой wandering для локальных ботов (вне сети) — выбираем точку в spawn-комнате и идём к ней.
function updateLocalBotsTick(
  runtime: MysteryRoomLocalRuntime,
  dtMs: number,
): Pick<MysteryRoomLocalRuntime, 'localBots' | 'hero' | 'impactEffects' | 'events'> {
  if (!runtime.localBots || runtime.localBots.length === 0) {
    return {
      localBots: runtime.localBots ?? [],
      hero: runtime.hero,
      impactEffects: runtime.impactEffects,
      events: runtime.events,
    };
  }
  const gameMode = runtime.gameplay.gameMode;
  const difficultyMultiplier = runtime.gameplay.botDifficulty === 'low'
    ? 0.8
    : runtime.gameplay.botDifficulty === 'high'
      ? 1.2
      : 1;
  const baseDetectionRange = gameMode === 'infection'
    ? 288
    : gameMode === 'survival'
      ? 420
      : gameMode === 'stealth'
        ? 170
        : 300;
  const contactDamage = Math.round((gameMode === 'infection' ? 15 : gameMode === 'survival' ? 10 : 6) * difficultyMultiplier);
  const contactCooldownMs = gameMode === 'infection' ? 600 : 900;
  const allowAggressiveChase = gameMode === 'infection' || gameMode === 'survival';
  const detectionRange = baseDetectionRange * difficultyMultiplier;
  const fallbackFloor = runtime.map.rooms[0]?.floor[0] ?? null;
  const dt = Math.max(0, dtMs);
  let hero = runtime.hero;
  let impactEffects = runtime.impactEffects;
  let events = runtime.events;
  const localBotSightSegments = buildSightBlockingSegments(runtime.map, runtime.doors);
  const localBots = runtime.localBots.map((bot) => {
    if (bot.remotePeer) {
      const px = bot.position.x;
      const py = bot.position.y;
      const dx = bot.target.x - px;
      const dy = bot.target.y - py;
      const dist = Math.hypot(dx, dy);
      const speed = Math.max(bot.speed ?? 110, 80);
      if (dist < 0.35) {
        return { ...bot, position: { ...bot.target }, animationState: 'idle' as const };
      }
      const step = Math.min(dist, (speed * dt) / 1000);
      const nx = px + (dx / dist) * step;
      const ny = py + (dy / dist) * step;
      const remain = Math.hypot(bot.target.x - nx, bot.target.y - ny);
      const deltaMove = Math.hypot(nx - px, ny - py);
      const facing = pickFacing(dx, dy);
      const moving = deltaMove > 0.06 || remain > 0.45;
      return {
        ...bot,
        position: { x: nx, y: ny },
        facing,
        animationState: moving ? ('walk' as const) : ('idle' as const),
      };
    }
    const patrolArea = bot.patrolArea ?? fallbackFloor;
    if (!patrolArea) {
      return bot;
    }
    const heroDx = hero.position.x - bot.position.x;
    const heroDy = hero.position.y - bot.position.y;
    const heroDist = Math.hypot(heroDx, heroDy);
    const seesHeroWithLos =
      !hero.hidden &&
      heroDist <= detectionRange * (bot.aggression || 1) &&
      hasUnobstructedSightLine(bot.position, hero.position, localBotSightSegments);
    const canChaseHero = allowAggressiveChase && hero.alive && seesHeroWithLos;
    const heroMovedForChase =
      bot.chasePathHeroPos &&
      Math.hypot(hero.position.x - bot.chasePathHeroPos.x, hero.position.y - bot.chasePathHeroPos.y) > LOCAL_BOT_CHASE_GOAL_SHIFT;
    const needChaseRecalc =
      canChaseHero &&
      (!bot.chaseSteerTarget ||
        runtime.elapsedMs >= (bot.chasePathRecalcAtMs ?? 0) ||
        Boolean(heroMovedForChase));
    let chaseSteerTarget = bot.chaseSteerTarget;
    let chasePathHeroPos = bot.chasePathHeroPos;
    let chasePathRecalcAtMs = bot.chasePathRecalcAtMs;
    if (canChaseHero && needChaseRecalc) {
      const route = buildActorRouteToPoint(
        runtime.map,
        runtime.doors,
        {
          accessLevels: hero.accessLevels.length > 0 ? [...hero.accessLevels] : (['public'] as MysteryRoomAccessLevel[]),
          actorType: 'bot',
          position: bot.position,
          zoneId: bot.zoneId,
        },
        hero.position,
        bot.radius,
      );
      chaseSteerTarget = route.reachable
        ? resolveImmediateRouteWaypoint(route, bot.position, bot.radius + 12) ?? { ...hero.position }
        : { ...hero.position };
      chasePathHeroPos = { ...hero.position };
      chasePathRecalcAtMs = runtime.elapsedMs + LOCAL_BOT_CHASE_ROUTE_MS;
    }
    const desiredTarget = canChaseHero ? (chaseSteerTarget ?? hero.position) : bot.target;
    const dx = desiredTarget.x - bot.position.x;
    const dy = desiredTarget.y - bot.position.y;
    const dist = Math.hypot(dx, dy);
    const needNewTarget = !canChaseHero && (dist < 6 || runtime.elapsedMs >= bot.decisionAtMs);
    const target = needNewTarget ? pickRoamPoint(patrolArea) : desiredTarget;
    const tdx = target.x - bot.position.x;
    const tdy = target.y - bot.position.y;
    const tdist = Math.hypot(tdx, tdy) || 1;
    const travelTime = tdist > 0.5 ? Math.min(tdist / bot.speed, dt / 1000) : 0;
    const vx = tdist > 0.5 ? (tdx / tdist) * bot.speed : 0;
    const vy = tdist > 0.5 ? (tdy / tdist) * bot.speed : 0;
    const velocity = { x: vx, y: vy };
    const nextPosition =
      travelTime > 0
        ? stepActor(runtime.map, { position: bot.position, radius: bot.radius }, velocity, travelTime, runtime.doors)
        : bot.position;
    const moved =
      Math.abs(nextPosition.x - bot.position.x) > 0.02 || Math.abs(nextPosition.y - bot.position.y) > 0.02;
    const facing = tdist > 0.5 ? pickFacing(tdx, tdy) : bot.facing;
    const chasePatch = canChaseHero
      ? { chaseSteerTarget, chasePathHeroPos, chasePathRecalcAtMs }
      : { chaseSteerTarget: undefined, chasePathHeroPos: undefined, chasePathRecalcAtMs: undefined };
    const updated = {
      ...bot,
      ...chasePatch,
      target,
      position: { ...nextPosition },
      zoneId: resolveZoneId(runtime.map, nextPosition),
      velocity,
      facing,
      animationState: moved && travelTime > 0 ? ('walk' as const) : ('idle' as const),
      decisionAtMs: canChaseHero
        ? runtime.elapsedMs + 420
        : needNewTarget
          ? runtime.elapsedMs + 1500 + Math.random() * 2500
          : bot.decisionAtMs,
    };
    const contactDistance = bot.radius + hero.radius + 2;
    if (canChaseHero && heroDist <= contactDistance) {
      if (runtime.elapsedMs - (bot.lastAttackAtMs ?? 0) >= contactCooldownMs) {
        const nextHealth = Math.max(0, hero.health - contactDamage);
        hero = {
          ...hero,
          health: nextHealth,
          alive: nextHealth > 0,
        };
        impactEffects = [
          {
            id: `bot_hit_${runtime.elapsedMs}_${impactEffects.length}`,
            kind: 'blood_splash' as const,
            position: { ...hero.position },
            radius: 24,
            createdAtMs: runtime.elapsedMs,
            ttlMs: 800,
            sourceId: bot.id,
          },
          ...impactEffects,
        ].slice(0, 42);
        events = [`${bot.name}: атака по игроку (-${contactDamage} HP)`, ...events].slice(0, 10);
        updated.lastAttackAtMs = runtime.elapsedMs;
      }
    }
    return updated;
  });
  return {
    localBots,
    hero,
    impactEffects,
    events,
  };
}

function pickRoamPoint(rect: { x: number; y: number; width: number; height: number }): MysteryRoomLocalBotState['target'] {
  const padding = Math.min(36, Math.max(0, rect.width / 2 - 8), Math.max(0, rect.height / 2 - 8));
  return {
    x: rect.x + padding + Math.random() * Math.max(0, rect.width - padding * 2),
    y: rect.y + padding + Math.random() * Math.max(0, rect.height - padding * 2),
  };
}

function pickFacing(dx: number, dy: number): MysteryRoomLocalBotState['facing'] {
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx >= 0 ? 'right' : 'left';
  }
  return dy >= 0 ? 'down' : 'up';
}

function resolveNpcTask(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
  doors: MysteryRoomLocalRuntime['doors'],
): MysteryRoomNpcState {
  const currentNpc =
    npc.currentTask &&
    shouldReleaseCurrentTask(runtime, npc) &&
    runtime.elapsedMs >= npc.taskLockedUntilMs
      ? {
          ...npc,
      currentTask: null,
      navPath: [],
      navIndex: 0,
      currentTargetNodeId: null,
      currentRoleAction: null,
      roleActionRemainingMs: 0,
      taskLockedUntilMs: 0,
      lastTaskRecalcAtMs: runtime.elapsedMs,
      stuckMs: 0,
      lastWaypointDistance: null,
        }
      : npc;
  const candidate = chooseNpcTask(runtime, currentNpc) ?? defaultRoleTask(runtime, currentNpc);
  if (!candidate) {
    return currentNpc;
  }

  if (!shouldReplaceCurrentTask(currentNpc.currentTask, candidate, runtime.elapsedMs, currentNpc.taskLockedUntilMs)) {
    if (
      currentNpc.currentTask &&
      currentNpc.currentTask.targetNodeId &&
      !currentNpc.currentTask.targetPoint &&
      currentNpc.navPath.length === 0
    ) {
      return applyTaskNavigation(runtime, currentNpc, currentNpc.currentTask, doors);
    }

    return currentNpc;
  }

  return applyTaskNavigation(runtime, currentNpc, candidate, doors);
}

function applyTaskNavigation(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
  task: MysteryRoomNpcTaskState,
  doors: MysteryRoomLocalRuntime['doors'],
): MysteryRoomNpcState {
  const navPath = task.targetNodeId ? buildNpcNavPath(runtime.map, doors, npc, task.targetNodeId) : [];
  const navIndex = navPath.length > 1 ? 1 : 0;

  return {
    ...npc,
    currentTask: task,
    taskLockedUntilMs: runtime.elapsedMs + task.minHoldMs,
    lastTaskRecalcAtMs: runtime.elapsedMs,
    navPath,
    navIndex,
    currentTargetNodeId: navPath.length > 0 ? task.targetNodeId : null,
    currentRoleAction: null,
    roleActionRemainingMs: 0,
    stuckMs: 0,
    lastWaypointDistance: null,
  };
}

function applyZombieContactDamage(
  hero: MysteryRoomLocalRuntime['hero'],
  npcs: MysteryRoomNpcState[],
  impactEffects: MysteryRoomImpactEffectState[],
  events: string[],
  nowMs: number,
): { hero: MysteryRoomLocalRuntime['hero']; npcs: MysteryRoomNpcState[]; impactEffects: MysteryRoomImpactEffectState[]; events: string[] } {
  let nextHero = hero;
  let nextImpactEffects = impactEffects;
  let nextEvents = events;
  const nextNpcs = npcs.map((npc) => ({ ...npc }));

  for (let index = 0; index < nextNpcs.length; index += 1) {
    const zombie = nextNpcs[index];
    if (!zombie.alive || !npcUsesZombieBehavior(zombie)) {
      continue;
    }
    const nextAttackAt = zombie.taskCooldowns.neutralize_intruder ?? 0;
    if (nowMs < nextAttackAt) {
      continue;
    }

    const heroDistance = Math.hypot(zombie.position.x - nextHero.position.x, zombie.position.y - nextHero.position.y);
    if (!nextHero.hidden && heroDistance <= zombie.radius + nextHero.radius + 10) {
      nextHero = {
        ...nextHero,
        health: Math.max(0, nextHero.health - 8),
      };
      nextImpactEffects = appendBloodEffects(nextImpactEffects, nowMs, {
        position: { ...nextHero.position },
        sourceId: 'hero',
        killed: nextHero.health <= 0,
      });
      nextNpcs[index] = {
        ...zombie,
        taskCooldowns: {
          ...zombie.taskCooldowns,
          neutralize_intruder: nowMs + 1_200,
        },
      };
      nextEvents = [`Зомби атакует: ${zombie.name} (-8 HP)`, ...nextEvents].slice(0, 10);
      continue;
    }

    const victimIndex = nextNpcs.findIndex((candidate) => {
      if (!npcCanTargetNpc(zombie, candidate)) {
        return false;
      }
      return Math.hypot(candidate.position.x - zombie.position.x, candidate.position.y - zombie.position.y) <= zombie.radius + candidate.radius + 8;
    });
    if (victimIndex < 0) {
      continue;
    }

    const victim = nextNpcs[victimIndex];
    const health = Math.max(0, victim.health - 16);
    const killed = health <= 0;
    nextNpcs[victimIndex] = {
      ...victim,
      health,
      alive: !killed,
      killedAtMs: killed ? nowMs : victim.killedAtMs,
      currentTask: killed ? null : victim.currentTask,
    };
    nextImpactEffects = appendBloodEffects(nextImpactEffects, nowMs, {
      position: { ...victim.position },
      sourceId: victim.id,
      killed,
    });
    nextNpcs[index] = {
      ...zombie,
      taskCooldowns: {
        ...zombie.taskCooldowns,
        neutralize_intruder: nowMs + 1_200,
      },
    };
    nextEvents = [`Зомби атакует: ${zombie.name} -> ${victim.name}`, ...nextEvents].slice(0, 10);
  }

  return {
    hero: nextHero,
    npcs: nextNpcs,
    impactEffects: nextImpactEffects,
    events: nextEvents,
  };
}

function applyHostileNpcContactDamage(
  hero: MysteryRoomLocalRuntime['hero'],
  npcs: MysteryRoomNpcState[],
  impactEffects: MysteryRoomImpactEffectState[],
  events: string[],
  nowMs: number,
): { hero: MysteryRoomLocalRuntime['hero']; npcs: MysteryRoomNpcState[]; impactEffects: MysteryRoomImpactEffectState[]; events: string[] } {
  let nextHero = hero;
  let nextImpactEffects = impactEffects;
  let nextEvents = events;
  const nextNpcs = npcs.map((npc) => ({ ...npc }));

  for (let index = 0; index < nextNpcs.length; index += 1) {
    const npc = nextNpcs[index];
    if (npcUsesZombieBehavior(npc) || !npcCanAttackHero(npc)) {
      continue;
    }
    const nextAttackAt = npc.taskCooldowns.neutralize_intruder ?? 0;
    if (nowMs < nextAttackAt) {
      continue;
    }

    const heroDistance = Math.hypot(npc.position.x - nextHero.position.x, npc.position.y - nextHero.position.y);
    if (nextHero.hidden || heroDistance > npc.radius + nextHero.radius + 14) {
      continue;
    }

    nextHero = {
      ...nextHero,
      health: Math.max(0, nextHero.health - 6),
    };
    nextImpactEffects = appendBloodEffects(nextImpactEffects, nowMs, {
      position: { ...nextHero.position },
      sourceId: 'hero',
      killed: nextHero.health <= 0,
    });
    nextNpcs[index] = {
      ...npc,
      taskCooldowns: {
        ...npc.taskCooldowns,
        neutralize_intruder: nowMs + 900,
      },
    };
    nextEvents = [`Охранник атакует: ${npc.name} (-6 HP)`, ...nextEvents].slice(0, 10);
  }

  return {
    hero: nextHero,
    npcs: nextNpcs,
    impactEffects: nextImpactEffects,
    events: nextEvents,
  };
}

function applyResolvedInjuryHealing(
  npcs: MysteryRoomNpcState[],
  incidents: MysteryRoomLocalRuntime['incidents'],
  events: string[],
): { npcs: MysteryRoomNpcState[]; events: string[] } {
  let nextEvents = events;
  const healedNpcIds = new Set(
    incidents
      .filter((incident) =>
        incident.kind === 'injured_person' &&
        incident.status === 'resolved' &&
        incident.resolutionAction === 'treat_injury' &&
        incident.sourceActorId !== 'system',
      )
      .map((incident) => incident.sourceActorId),
  );

  if (healedNpcIds.size === 0) {
    return { npcs, events };
  }

  const nextNpcs = npcs.map((npc) => {
    if (!healedNpcIds.has(npc.id) || !npc.alive || npc.health >= npc.maxHealth) {
      return npc;
    }

    nextEvents = [`Медик стабилизировал: ${npc.name}`, ...nextEvents].slice(0, 10);
    return {
      ...npc,
      health: npc.maxHealth,
      currentTask: npc.currentTask?.kind === 'evacuate' && !npc.currentTask.assignedIncidentId ? null : npc.currentTask,
      navPath: npc.currentTask?.kind === 'evacuate' && !npc.currentTask.assignedIncidentId ? [] : npc.navPath,
      navIndex: npc.currentTask?.kind === 'evacuate' && !npc.currentTask.assignedIncidentId ? 0 : npc.navIndex,
      currentTargetNodeId: npc.currentTask?.kind === 'evacuate' && !npc.currentTask.assignedIncidentId ? null : npc.currentTargetNodeId,
    };
  });

  return {
    npcs: nextNpcs,
    events: nextEvents,
  };
}

function appendBloodEffects(
  impactEffects: MysteryRoomImpactEffectState[],
  nowMs: number,
  options: { position: { x: number; y: number }; sourceId: string; killed: boolean },
) {
  const bloodSplash: MysteryRoomImpactEffectState = {
    id: `zombie_blood_splash_${nowMs}_${impactEffects.length}`,
    kind: 'blood_splash',
    position: options.position,
    radius: 34,
    createdAtMs: nowMs,
    ttlMs: 520,
    sourceId: options.sourceId,
  };
  const nextEffects = [...impactEffects, bloodSplash];
  if (options.killed) {
    nextEffects.push({
      id: `zombie_blood_pool_${nowMs}_${impactEffects.length + 1}`,
      kind: 'blood_pool',
      position: options.position,
      radius: 44,
      createdAtMs: nowMs,
      ttlMs: 60_000,
      sourceId: options.sourceId,
    });
  }
  return nextEffects.slice(-48);
}

function finalizeHeroLife(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  if (runtime.hero.health > 0 && runtime.hero.alive) {
    return runtime;
  }

  const heroWasAlive = runtime.hero.alive;
  const respawn = runtime.respawn ?? createHeroRespawn(runtime);
  return {
    ...runtime,
    gameStatus: 'respawning',
    respawn,
    hero: {
      ...runtime.hero,
      health: Math.max(0, runtime.hero.health),
      alive: false,
      velocity: { x: 0, y: 0 },
      movementMode: 'idle',
      animationState: 'idle',
      hidden: false,
      hideZoneId: null,
    },
    input: {
      ...runtime.input,
      move: { x: 0, y: 0 },
      pointerTarget: null,
    },
    events: heroWasAlive
      ? [`Герой погиб. Респавн через ${Math.round(HERO_RESPAWN_DELAY_MS / 1000)} сек.`, ...runtime.events].slice(0, 10)
      : runtime.events,
  };
}

function advanceHeroRespawn(runtime: MysteryRoomLocalRuntime, dtMs: number): MysteryRoomLocalRuntime {
  const elapsedMs = runtime.elapsedMs + dtMs;
  const respawn = runtime.respawn ?? createHeroRespawn(runtime);

  if (elapsedMs < respawn.readyAtMs) {
    return {
      ...runtime,
      elapsedMs,
      respawn,
      input: {
        ...runtime.input,
        move: { x: 0, y: 0 },
        pointerTarget: null,
      },
    };
  }

  return {
    ...runtime,
    gameStatus: 'running',
    elapsedMs,
    respawn: null,
    hero: {
      ...runtime.hero,
      position: { ...respawn.checkpoint },
      velocity: { x: 0, y: 0 },
      facing: 'down',
      zoneId: respawn.zoneId,
      hidden: false,
      hideZoneId: null,
      movementMode: 'idle',
      animationState: 'idle',
      alertState: 'clear',
      spottedByNpcIds: [],
      health: runtime.hero.maxHealth,
      alive: true,
      carriedObjectId: null,
      carriedObjectWeightKg: 0,
    },
    input: {
      ...runtime.input,
      move: { x: 0, y: 0 },
      pointerTarget: null,
    },
    events: ['Герой возродился на контрольной точке', ...runtime.events].slice(0, 10),
  };
}

function createHeroRespawn(runtime: MysteryRoomLocalRuntime) {
  const checkpoint = resolveHeroRespawnPoint(runtime);
  return {
    startedAtMs: runtime.elapsedMs,
    readyAtMs: runtime.elapsedMs + HERO_RESPAWN_DELAY_MS,
    checkpoint,
    zoneId: resolveZoneId(runtime.map, checkpoint),
  };
}

function resolveHeroRespawnPoint(runtime: MysteryRoomLocalRuntime) {
  const checkpoints = runtime.map.workAnchors
    .filter((anchor) => anchor.kind === 'checkpoint')
    .map((anchor) => anchor.position);
  const spawns = runtime.map.spawnSlots.map((slot) => ({ x: slot.x, y: slot.y }));
  const candidates = checkpoints.length > 0 ? checkpoints : spawns.length > 0 ? spawns : [runtime.map.spawn];

  return nearestPoint(candidates, runtime.hero.position);
}

function nearestPoint(points: Array<{ x: number; y: number }>, origin: { x: number; y: number }) {
  return points.reduce((best, point) => {
    const bestDistance = Math.hypot(best.x - origin.x, best.y - origin.y);
    const distance = Math.hypot(point.x - origin.x, point.y - origin.y);
    return distance < bestDistance ? point : best;
  }, points[0] ?? origin);
}

function actorBoundsForCircle(
  actorId: string,
  position: { x: number; y: number },
  radius: number,
  accessLevels: MysteryRoomAccessLevel[],
  actorType?: DoorActor['actorType'],
  inventoryItemIds: string[] = [],
): DoorActor {
  return {
    actorId,
    actorType,
    bounds: {
      x: position.x - radius,
      y: position.y - radius,
      width: radius * 2,
      height: radius * 2,
    },
    accessLevels,
    inventoryItemIds,
  };
}
